import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Icon from '../Icon';
import { inkA, orange, tokens } from '../tokens';

// Gen-3 labelled checkbox: a real (visually hidden) <input type="checkbox">
// inside its <label>, drawn as a 20px rounded box that fills orange with a
// white check when on. Keyboard and screen readers get the native control;
// the orange halo shows on keyboard focus only.
//   onChange(nextChecked, event)
//   boxed  — draws the whole row as a bordered option card (option lists)
//   error  — red box border and aria-invalid
const ERROR_RED = '#ED4337';

const Root = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  color: ${(p) => tokens(p.theme).text};
  font-size: 14px;
  line-height: 1.4;
  -webkit-tap-highlight-color: transparent;

  ${(p) =>
    p.$boxed &&
    `
    padding: 11px 13px;
    border-radius: 8px;
    border: 1px solid ${
      p.$checked ? tokens(p.theme).primary : inkA(p.theme, 0.12)
    };
    background: ${p.$checked ? orange(0.12) : inkA(p.theme, 0.04)};
    transition: background 150ms ease, border-color 150ms ease;
  `}
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  padding: 0;
  opacity: 0;
`;

const Box = styled.span`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 2px solid
    ${(p) => {
      if (p.$checked) return tokens(p.theme).primary;
      return p.$error ? ERROR_RED : inkA(p.theme, 0.3);
    }};
  background: ${(p) => (p.$checked ? tokens(p.theme).primary : 'transparent')};
  transition: background 150ms ease, border-color 150ms ease;

  ${HiddenInput}:focus-visible + & {
    box-shadow: 0 0 0 3px ${orange(0.45)};
  }
`;

const Text = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Checkbox = ({
  checked,
  onChange,
  label,
  disabled,
  error,
  boxed,
  id,
  describedBy,
  className,
}) => (
  <Root
    className={className}
    $boxed={boxed}
    $checked={checked}
    $disabled={disabled}
  >
    <HiddenInput
      type="checkbox"
      id={id}
      checked={checked}
      disabled={disabled}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      onChange={(e) => {
        if (onChange) onChange(e.target.checked, e);
      }}
    />
    <Box aria-hidden="true" $checked={checked} $error={error}>
      {checked ? <Icon name="check" size={14} strokeWidth={3} /> : null}
    </Box>
    <Text>{label}</Text>
  </Root>
);

Checkbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  label: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  boxed: PropTypes.bool,
  id: PropTypes.string,
  describedBy: PropTypes.string,
  className: PropTypes.string,
};

Checkbox.defaultProps = {
  onChange: undefined,
  disabled: false,
  error: false,
  boxed: false,
  id: undefined,
  describedBy: undefined,
  className: undefined,
};

export default Checkbox;
